import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios';
import { enqueueSnack } from './snackbar';

const normalizeUuid = (value) => value == null ? value : String(value);

const sanitizeUuidArray = (selectedids) => {
    if(selectedids == null){
        return selectedids;
    }

    const list = Array.isArray(selectedids) ? selectedids : [selectedids];

    return list
        .map(normalizeUuid)
        .map((value) => value == null ? value : value.trim())
        .filter((value) => value !== '' && value != null);
};

const normalizePost = (post) => {
    if(!post){
        return post;
    }

    const resolvedUuid = post._id ?? post.uuid;

    return {
        ...post,
        uuid: normalizeUuid(resolvedUuid)
    };
};

const buildInitialPanelState = () => ({
    items: [],
    total: 0,
    offset: 0,
    limit: 100,
    loading: false,
    error: null,
    lastRequest: null
});

const initialSplitState = () => ({
    recent: buildInitialPanelState(),
    engagement: buildInitialPanelState()
});

export const slice = createSlice({
    name: 'post',
    initialState: {
        viewType: 'grid',
        filteredList: [],
        filters: {

        },
        loading: false,
        error: false,
        previewTranslations: {},
        previewSummaries: {},
        selected: [],
        lastSingleRequest: null,
        split: initialSplitState()

    },
    reducers: {
        addToSelection: (state, action) => {
            state.selected = [
                ...state.selected || [],
                normalizeUuid(action.payload)
            ]
        },
        removeFromSelection: (state, action) => {
            const targetUuid = normalizeUuid(action.payload);
            state.selected = state.selected.filter(s => s !== targetUuid);
        },
        clearSelection: (state, action) => {
            state.selected = [];
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        clearPosts: state => {
            state.filteredList = [];
            state.filteredListTotal = 0;
            state.filteredListOffset = 0;
            state.filteredListLimit = 100;
            state.split = initialSplitState();
        },
        setViewType: (state, action) => {
            state.viewType = action.payload;
        },
        toggleViewType: (state, action) => {
            state.viewType = state.viewType==="grid" ? "list" : "grid";
        },
        resetSplitState: (state) => {
            state.split = initialSplitState();
        }
    },
    extraReducers(builder) {
        builder.addCase(translatePost.pending, (state, action) => {
            const args = action.meta.arg || {};
            if(args.intent === 'preview' && args.uuid){
                state.previewTranslations = {
                    ...state.previewTranslations,
                    [args.uuid]: null
                };
            }
        });
        builder.addCase(translatePost.fulfilled, (state, action) => {
            const {payload} = action;
            const args = action.meta.arg;
            if(args && args.intent === 'preview'){
                if(args.uuid){
                    state.previewTranslations = {
                        ...state.previewTranslations,
                        [args.uuid]: payload
                    };
                }
                return;
            }
            state.filteredList = (state.filteredList || []).map(p => {
                if(p.uuid === args.uuid){
                    return {
                        ...p,
                        translated: payload
                    }
                }
                return p;
            })

        });

        builder.addCase(searchPost.fulfilled, (state, action) => {
            const {payload} = action;
            const args = action.meta.arg || {};
            state.filters = args;
            state.lastSingleRequest = args;
            state.filteredListOffset = args.offset || 0;
            state.filteredListLimit = args.limit || 100;
            state.filteredList = ((payload && payload.posts) || []).map(normalizePost);
            state.filteredListTotal = (payload && payload.total) || 0;
        })

        builder.addCase(searchSplitPanel.pending, (state, action) => {
            const panel = action.meta.arg && action.meta.arg.panel;
            if(panel && state.split[panel]){
                state.split[panel].loading = true;
                state.split[panel].error = null;
            }
        })

        builder.addCase(searchSplitPanel.fulfilled, (state, action) => {
            const panel = action.meta.arg && action.meta.arg.panel;
            const requestFilters = (action.meta.arg && action.meta.arg.filters) || {};
            const baseFilters = (action.meta.arg && action.meta.arg.baseFilters) || requestFilters || {};
            if(panel && state.split[panel]){
                state.split[panel].loading = false;
                state.split[panel].items = ((action.payload && action.payload.posts) || []).map(normalizePost);
                state.split[panel].total = (action.payload && action.payload.total) || 0;
                state.split[panel].offset = requestFilters.offset || 0;
                state.split[panel].limit = requestFilters.limit || state.split[panel].limit || 100;
                state.split[panel].lastRequest = {
                    panel,
                    filters: requestFilters,
                    baseFilters
                };
            }
            state.filters = baseFilters;
        })

        builder.addCase(searchSplitPanel.rejected, (state, action) => {
            const panel = action.meta.arg && action.meta.arg.panel;
            if(panel && state.split[panel]){
                state.split[panel].loading = false;
                state.split[panel].error = action.error && action.error.message || 'Error loading panel';
            }
        })

        builder.addCase(summarizePost.fulfilled, (state, action) => {
            const payload = action.payload || {};
            const args = action.meta.arg || {};
            const summary = payload.summary ?? payload;
            const targetUuid = args.uuid || payload.uuid;

            if(args.intent === 'preview'){
                if(targetUuid){
                    state.previewSummaries = {
                        ...state.previewSummaries,
                        [targetUuid]: summary
                    };
                }
                return;
            }

            if(targetUuid){
                state.filteredList = (state.filteredList || []).map(p => {
                    if(p.uuid === targetUuid){
                        return {
                            ...p,
                            post_summary: summary
                        }
                    }
                    return p;
                })
            }
        })

        builder.addCase(summarizePost.pending, (state, action) => {
            const args = action.meta.arg || {};
            if(args.intent === 'preview' && args.uuid){
                state.previewSummaries = {
                    ...state.previewSummaries,
                    [args.uuid]: null
                };
            }
        })

      }
    
})
export const reloadPosts = createAsyncThunk('post/reload', async (filters, store) => {
    const state = store.getState();
    return state.dispatch(searchPost(state.post.filters));
});

export const searchPost = createAsyncThunk('post/search', async (filters, store) => {
    const payload = {
        limit: 100,
        orderBy: 'post_timestamp',
        ...filters
    };

    store.dispatch(slice.actions.setError(false));
    store.dispatch(slice.actions.setLoading(true));
    
    return axios.post("/post/search", payload)
    .then((response) => {
        const data = response.data;
        if(data.error){
            store.dispatch(enqueueSnack({
                message: data.error,
                options: {
                    variant: 'error'
                }
            }))
        }
        return data;
    })
    .catch((e) => {
        store.dispatch(enqueueSnack({
            message: e.message,
            options: {
                variant: 'error'
            }
        }))
    })
    .finally(() => {
        store.dispatch(slice.actions.setLoading(false));
    })
})

export const searchSplitPanel = createAsyncThunk('post/searchSplitPanel', async ({panel, filters, baseFilters}, store) => {
    const payload = {
        limit: 100,
        orderBy: panel === 'recent' ? 'post_timestamp' : 'engagement',
        ...filters
    };

    store.dispatch(slice.actions.setError(false));
    
    return axios.post("/post/search", payload)
    .then((response) => {
        const data = response.data;
        if(data.error){
            store.dispatch(enqueueSnack({
                message: data.error,
                options: {
                    variant: 'error'
                }
            }))
        }
        return data;
    })
    .catch((e) => {
        store.dispatch(enqueueSnack({
            message: e.message,
            options: {
                variant: 'error'
            }
        }))
    })
})

export const editPosts = createAsyncThunk('post/edit-bulk', async (params, store) => {
    const state = store.getState();
    const selectedids = sanitizeUuidArray(params && params.selectedids);
    const payload = {
        ...params,
        ...(selectedids !== undefined ? { selectedids } : {}),
        tab_id: state.project.activeTab
    };

    return axios.post("/post/edit-bulk", payload)
    .then((response) => {
        const data = response.data;
        if(data.error){
            store.dispatch(enqueueSnack({
                message: data.error,
                options: {
                    variant: 'error'
                }
            }))
        }
        return data;
    })
    .catch((e) => {
        store.dispatch(enqueueSnack({
            message: e.message,
            options: {
                variant: 'error'
            }
        }))
    })
})

export const deletePosts = createAsyncThunk('post/delete', async(params,store) => {

    
    store.dispatch(slice.actions.setError(false));
    store.dispatch(slice.actions.setLoading(true));
    
    const selectedPosts = store.getState().post.selected;
    const projectId = store.getState().project.single.id;
    const filters = store.getState().post.filters;
    const tab_id = store.getState().project.activeTab;
    
    return axios.post('/post/delete', { "projectId":projectId, "uuidArray":selectedPosts, "tab_id":tab_id })
    .then((response) => {
        const data = response.data;
        if(data.error){
            store.dispatch(enqueueSnack({
                message: data.error,
                options: {
                    variant: 'error'
                }
            }))
        }
        return data;
    })
    .catch((e) => {
        store.dispatch(enqueueSnack({
            message: e.message,
            options: {
                variant: 'error'
            }
        }))
    })
    .finally(() => {
        setTimeout(() => {
            store.dispatch(clearSelection());
            const state = store.getState();
            const project = state.project.single;
            const activeTabId = state.project.activeTab;
            const tab = project && project.tabs && project.tabs.find(t => t.id === activeTabId);
            const tabFilters = tab && tab.config && tab.config.filters || {};
            const viewMode = tabFilters.viewMode || (tabFilters.orderBy === 'engagement' ? 'engagement' : 'date');

            if(viewMode === 'split'){
                const recentReq = state.post.split.recent.lastRequest;
                const engagementReq = state.post.split.engagement.lastRequest;
                if(recentReq){
                    store.dispatch(searchSplitPanel(recentReq));
                }
                if(engagementReq){
                    store.dispatch(searchSplitPanel(engagementReq));
                }
            }else{
                const fallbackFilters = {
                    ...tabFilters,
                    orderBy: viewMode === 'engagement' ? 'engagement' : 'post_timestamp',
                    offset: tabFilters.offset || 0,
                    limit: tabFilters.limit || 100,
                    project_id: project && project.id,
                    tab_id: tab && tab.id
                };
                store.dispatch(searchPost(state.post.lastSingleRequest || fallbackFilters));
            }
            store.dispatch(slice.actions.setLoading(false));
        },1000); //elastic non fa un update immediato
    })
})

export const translatePost = createAsyncThunk('post/translate', async (params, store) => {
    return axios.post("/post/translate", params)
    .then((response) => {
        const data = response.data;
        if(data.error){
            store.dispatch(enqueueSnack({
                message: data.error,
                options: {
                    variant: 'error'
                }
            }))
        }
        return data;
    })
    .catch((e) => {
        store.dispatch(enqueueSnack({
            message: e.message,
            options: {
                variant: 'error'
            }
        }))
    })
})

export const summarizePost = createAsyncThunk('post/summarize', async (params, store) => {
    return axios.post("/post/summarize", params)
    .then((response) => {
        const data = response.data; // includes summary and uuid
        if(data.error){
            store.dispatch(enqueueSnack({
                message: data.error,
                options: {
                    variant: 'error'
                }
            }))
        }
        return data;
    })
    .catch((e) => {
        store.dispatch(enqueueSnack({
            message: e.message,
            options: {
                variant: 'error'
            }
        }))
    })
})

export const {
    clearPosts, 
    clearSelection, 
    addToSelection, 
    removeFromSelection, 
    setError, 
    setLoading, 
    setViewType,
    toggleViewType,
    resetSplitState,
} = slice.actions

export default slice.reducer
